/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {
  Avatar,
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  StyleRules,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Theme,
  Typography
} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import {AddCircleOutline, ArrowDownward, ArrowUpward, CloudUpload, Delete, DeleteForever, Edit} from "@material-ui/icons";
import {Alert} from "@material-ui/lab";
import classNames from 'classnames';
import React, {memo, useEffect, useRef, useState} from 'react';
import {Trans, useTranslation} from "react-i18next";
import {apiUrl} from "../../utils/api";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type GalleriesAdminClassKey =
  | 'root' | 'galleryTabs' | 'tableContainer' | 'thumb' | 'section' | 'sectionTitle'
  | 'dropzone' | 'dropzoneActive' | 'dropzoneUploading' | 'dropzoneIcon' | 'dropzoneHint' | 'browseLink'
  | 'centeredCell';

const useStyles = makeStyles<Theme, {}, GalleriesAdminClassKey>(
  (): StyleRules<GalleriesAdminClassKey> =>
    createStyles({
      root: {},
      galleryTabs: {
        '& .MuiTab-textColorPrimary': {
          color: 'var(--color-text-muted)'
        },
        '& .MuiTab-textColorPrimary.Mui-selected': {
          color: 'var(--color-text-strong)'
        }
      },
      tableContainer: {
        marginBottom: '20px'
      },
      thumb: {
        width: '64px',
        height: '48px',
        objectFit: 'cover',
        borderRadius: '4px'
      },
      section: {
        marginTop: '32px'
      },
      sectionTitle: {
        marginBottom: '12px'
      },
      dropzone: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '28px 16px',
        marginBottom: '20px',
        borderRadius: '8px',
        border: '2px dashed var(--color-border)',
        color: 'var(--color-text-muted)',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, background 0.15s ease, color 0.15s ease',
        '&:hover': {
          borderColor: 'var(--color-text-muted)',
          color: 'var(--color-text)'
        }
      },
      dropzoneActive: {
        borderColor: 'var(--color-text-strong)',
        background: 'var(--color-hover-tint)',
        color: 'var(--color-text-strong)'
      },
      dropzoneUploading: {
        cursor: 'default',
        pointerEvents: 'none'
      },
      dropzoneIcon: {
        fontSize: '32px',
        opacity: 0.7
      },
      dropzoneHint: {
        fontSize: '0.8rem',
        color: 'var(--color-text-dim)'
      },
      browseLink: {
        color: 'var(--color-text-strong)',
        textDecoration: 'underline',
        cursor: 'pointer'
      },
      centeredCell: {
        textAlign: 'center'
      }
    }),
  {
    name: 'GalleriesAdmin'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Types
//----------------------------------------------------------------------------------------------------------------------
type GallerySlug = 'nature' | 'urban' | 'myWorld';

const GALLERIES: {slug: GallerySlug, labelKey: string}[] = [
  {slug: 'nature', labelKey: 'home.nature'},
  {slug: 'urban', labelKey: 'home.urban'},
  {slug: 'myWorld', labelKey: 'home.myWorld'}
];

type GalleryImageRow = {
  id: number;
  filename: string;
  img: string;
  title: string;
  cols: number;
};

type OrphanFile = {
  filename: string;
  img: string;
};

type SnackState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type GalleriesAdminProps = {
  className?: string;
  // Called whenever a request comes back 401, so the parent Admin page can
  // drop back to the login screen (a gallery-admin session can expire the
  // same way the guest-book one can).
  onUnauthenticated: () => void;
};

export default memo(({className, onUnauthenticated}: GalleriesAdminProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [galleryTab, setGalleryTab] = useState<number>(0);
  const gallery = GALLERIES[galleryTab].slug;

  const [images, setImages] = useState<GalleryImageRow[]>([]);
  const [orphans, setOrphans] = useState<OrphanFile[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [editRow, setEditRow] = useState<GalleryImageRow | null>(null);
  const [savingEdit, setSavingEdit] = useState<boolean>(false);
  const [removeRow, setRemoveRow] = useState<GalleryImageRow | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);
  const [deleteOrphan, setDeleteOrphan] = useState<OrphanFile | null>(null);
  const [deletingOrphan, setDeletingOrphan] = useState<boolean>(false);
  const [attachingFilename, setAttachingFilename] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);

  const [snack, setSnack] = useState<SnackState>({open: false, message: '', severity: 'success'});
  const closeSnack = () => setSnack((s) => ({...s, open: false}));
  const notify = (message: string, severity: SnackState['severity'] = 'success') =>
    setSnack({open: true, message, severity});

  const loadImages = () => {
    setLoading(true);
    fetch(apiUrl(`api/admin/listGalleryImages.php?gallery=${gallery}`), {credentials: 'include'})
    .then(async (response) => {
      if (response.status === 401) {
        onUnauthenticated();
        return;
      }
      const d = await response.json();
      if (Array.isArray(d.images) && Array.isArray(d.orphans)) {
        setImages(d.images);
        setOrphans(d.orphans);
      } else {
        setImages([]);
        setOrphans([]);
        notify(d.error || t('admin.galleries.loadError'), 'error');
      }
    })
    .catch(() => notify(t('admin.galleries.loadError'), 'error'))
    .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallery]);

  const handleGalleryTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGalleryTab(newValue);
  };

  const handleSaveEdit = () => {
    if (!editRow || savingEdit) {
      return;
    }
    setSavingEdit(true);

    const formData = new FormData();
    formData.append('id', String(editRow.id));
    formData.append('title', editRow.title);
    formData.append('cols', String(editRow.cols));

    fetch(apiUrl('api/admin/updateGalleryImage.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (d.success) {
        notify(t('admin.galleries.updateSuccess'));
        setEditRow(null);
        loadImages();
      } else {
        notify(d.error || t('admin.galleries.updateError'), 'error');
      }
    })
    .catch(() => notify(t('admin.galleries.updateError'), 'error'))
    .finally(() => setSavingEdit(false));
  };

  const handleConfirmRemove = () => {
    if (!removeRow || removing) {
      return;
    }
    setRemoving(true);

    const formData = new FormData();
    formData.append('id', String(removeRow.id));

    fetch(apiUrl('api/admin/removeGalleryImage.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (d.success) {
        notify(t('admin.galleries.removeSuccess'));
        setRemoveRow(null);
        loadImages();
      } else {
        notify(d.error || t('admin.galleries.removeError'), 'error');
      }
    })
    .catch(() => notify(t('admin.galleries.removeError'), 'error'))
    .finally(() => setRemoving(false));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= images.length) {
      return;
    }
    const reordered = [...images];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setImages(reordered);

    const formData = new FormData();
    formData.append('gallery', gallery);
    formData.append('orderedIds', JSON.stringify(reordered.map((r) => r.id)));

    fetch(apiUrl('api/admin/reorderGalleryImages.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (!d.success) {
        notify(d.error || t('admin.galleries.reorderError'), 'error');
      }
    })
    .catch(() => notify(t('admin.galleries.reorderError'), 'error'))
    .finally(() => loadImages());
  };

  const handleAddExisting = (orphan: OrphanFile) => {
    if (attachingFilename) {
      return;
    }
    setAttachingFilename(orphan.filename);

    const formData = new FormData();
    formData.append('gallery', gallery);
    formData.append('filename', orphan.filename);

    fetch(apiUrl('api/admin/addExistingGalleryImage.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (d.success) {
        notify(t('admin.galleries.addSuccess'));
        loadImages();
      } else {
        notify(d.error || t('admin.galleries.addError'), 'error');
      }
    })
    .catch(() => notify(t('admin.galleries.addError'), 'error'))
    .finally(() => setAttachingFilename(null));
  };

  const handleConfirmDeleteFile = () => {
    if (!deleteOrphan || deletingOrphan) {
      return;
    }
    setDeletingOrphan(true);

    const formData = new FormData();
    formData.append('gallery', gallery);
    formData.append('filename', deleteOrphan.filename);

    fetch(apiUrl('api/admin/deleteGalleryImageFile.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (d.success) {
        notify(t('admin.galleries.fileDeleted'));
        setDeleteOrphan(null);
        loadImages();
      } else {
        notify(d.error || t('admin.galleries.deleteError'), 'error');
      }
    })
    .catch(() => notify(t('admin.galleries.deleteError'), 'error'))
    .finally(() => setDeletingOrphan(false));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const uploadFile = (file: File) => {
    if (uploading) {
      return;
    }
    setUploading(true);

    const formData = new FormData();
    formData.append('gallery', gallery);
    formData.append('file', file);

    fetch(apiUrl('api/admin/uploadGalleryImage.php'), {method: 'post', body: formData, credentials: 'include'})
    .then((response) => response.json())
    .then((d) => {
      if (d.success) {
        notify(t('admin.galleries.uploadSuccess'));
        loadImages();
      } else {
        notify(d.error || t('admin.galleries.uploadError'), 'error');
      }
    })
    .catch(() => notify(t('admin.galleries.uploadError'), 'error'))
    .finally(() => setUploading(false));
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!uploading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  return (
    <div className={classNames(classes.root, className)}>
      <Tabs
        value={galleryTab}
        onChange={handleGalleryTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.galleryTabs}
      >
        {GALLERIES.map((g) => (
          <Tab key={g.slug} label={t(g.labelKey)} />
        ))}
      </Tabs>

      <div
        className={classNames(
          classes.section,
          classes.dropzone,
          dragActive && classes.dropzoneActive,
          uploading && classes.dropzoneUploading
        )}
        onClick={handleUploadClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleUploadClick();
          }
        }}
      >
        {uploading ? <CircularProgress size={28} /> : <CloudUpload className={classes.dropzoneIcon} />}
        <Typography>
          {uploading ? (
            t('admin.galleries.dropzone.uploading')
          ) : (
            <Trans i18nKey="admin.galleries.dropzone.text" components={{link: <span className={classes.browseLink} />}} />
          )}
        </Typography>
        <Typography className={classes.dropzoneHint}>{t('admin.galleries.dropzone.hint')}</Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{display: 'none'}}
          onChange={handleFileSelected}
        />
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.galleries.table.preview')}</TableCell>
              <TableCell>{t('admin.galleries.table.title')}</TableCell>
              <TableCell>{t('admin.galleries.table.layout')}</TableCell>
              <TableCell align="right">{t('admin.galleries.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {images.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>
                  <img className={classes.thumb} src={asset(row.img)} alt={row.title} />
                </TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>{row.cols === 2 ? t('admin.galleries.layout.wide') : t('admin.galleries.layout.normal')}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="move up"
                    disabled={index === 0}
                    onClick={() => handleMove(index, -1)}
                  >
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="move down"
                    disabled={index === images.length - 1}
                    onClick={() => handleMove(index, 1)}
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="edit" onClick={() => setEditRow(row)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="remove" onClick={() => setRemoveRow(row)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && images.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className={classes.centeredCell}>{t('admin.galleries.noImages')}</TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={4} className={classes.centeredCell}><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>{t('admin.galleries.availableFiles.title')}</Typography>
        <Typography variant="body2" color="textSecondary" className={classes.sectionTitle}>
          {t('admin.galleries.availableFiles.description')}
        </Typography>
        <Paper>
          <List dense>
            {orphans.map((orphan) => (
              <ListItem key={orphan.filename}>
                <ListItemAvatar>
                  <Avatar variant="square" src={asset(orphan.img)} />
                </ListItemAvatar>
                <ListItemText primary={orphan.filename} />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    aria-label="add to gallery"
                    disabled={attachingFilename === orphan.filename}
                    onClick={() => handleAddExisting(orphan)}
                  >
                    <AddCircleOutline fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="delete permanently" onClick={() => setDeleteOrphan(orphan)}>
                    <DeleteForever fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {orphans.length === 0 && (
              <ListItem>
                <ListItemText primary={t('admin.galleries.availableFiles.empty')} />
              </ListItem>
            )}
          </List>
        </Paper>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('admin.galleries.editDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t('admin.galleries.editDialog.titleLabel')}
            value={editRow?.title || ''}
            onChange={(e) => setEditRow((r) => r && {...r, title: e.target.value})}
          />
          <FormControl fullWidth margin="dense">
            <Select
              value={editRow?.cols || 1}
              onChange={(e) => setEditRow((r) => r && {...r, cols: Number(e.target.value)})}
            >
              <MenuItem value={1}>{t('admin.galleries.layout.normal')}</MenuItem>
              <MenuItem value={2}>{t('admin.galleries.layout.wide')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>{t('admin.common.cancel')}</Button>
          <Button color="primary" onClick={handleSaveEdit} disabled={savingEdit || !editRow?.title}>
            {savingEdit ? <CircularProgress size={20} /> : t('admin.common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove-from-gallery confirmation */}
      <Dialog open={!!removeRow} onClose={() => setRemoveRow(null)}>
        <DialogTitle>{t('admin.galleries.removeDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('admin.galleries.removeDialog.body')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveRow(null)}>{t('admin.common.cancel')}</Button>
          <Button color="secondary" onClick={handleConfirmRemove} disabled={removing}>
            {removing ? <CircularProgress size={20} /> : t('admin.common.remove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent file deletion confirmation */}
      <Dialog open={!!deleteOrphan} onClose={() => setDeleteOrphan(null)}>
        <DialogTitle>{t('admin.galleries.deleteFileDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            <Trans
              i18nKey="admin.galleries.deleteFileDialog.body"
              values={{filename: deleteOrphan?.filename}}
              components={{strong: <strong />}}
            />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOrphan(null)}>{t('admin.common.cancel')}</Button>
          <Button color="secondary" onClick={handleConfirmDeleteFile} disabled={deletingOrphan}>
            {deletingOrphan ? <CircularProgress size={20} /> : t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert onClose={closeSnack} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
});
